import Component from '../Component';

/** Pagination logic (Infinite scrolling and Load more button) */
export default class Pagination extends Component {
  // how many pixels from bottom should next page start loading
  PAGINATION_LOAD_OFFSET = 1200;
  // how often should the scroll position should be checked (in milliseconds)
  SCROLL_DELAY = 200;

  PAGINATION_SELECTOR = '.pagination';
  COLLECTION_WRAPPER_SELECTOR = '#CollectionProductGrid';
  COLLECTION_MAIN_SELECTOR = '.collection-main-body-inner';
  COLLECTION_HEADER_SELECTOR = '.collection-main-central-header';
  COLLECTION_ITEM_SELECTOR = '.grid__item';
  COLLECTION_ITEM_EXCLUDE_SELECTOR = '.collection-central-description-block';

  /**
   * @param {import('../ThemeBase').default} theme
   * @param {HTMLElement} element
   */
  constructor(theme, element, loadedCallback1, loadedCallback2) {
    super(theme, element);
    this.loadedCallback1 = loadedCallback1;
    this.loadedCallback2 = loadedCallback2;
    this.$self = this;
    this.$collection = document.querySelector(this.COLLECTION_MAIN_SELECTOR);
    this.$wrapper = document.querySelector(this.COLLECTION_WRAPPER_SELECTOR);
    this.loading = false;
    this.fully_loaded = false;
    this.scroll_timeout = null;
    this.pagination_type = null;

    // If neither "infinite scrolling" nor "load more" are enabled, end function here
    const paginationInfinite = document.querySelector('.pagination-infinite');
    const paginationLoadMore = document.querySelector('.pagination--load-more');

    this.initialize();
    if (paginationInfinite || paginationLoadMore) {
      this.scroll_position_history();
    }
  }

  initialize = () => {
    const pagination = document.querySelector('.pagination');
    if (pagination && pagination.classList.contains('pagination-infinite')) {
      this.pagination_type = 'infinite-scroll';

    } else if (pagination && pagination.classList.contains('pagination--load-more')) {
      this.pagination_type = 'load-more';

      this.load_more_button();
    }

    this.execCallbacks();
  }

  execCallbacks = () => {
    if (this.loadedCallback1) {
      this.loadedCallback1();
    }
    if (this.loadedCallback2) {
      this.loadedCallback2();
    }
  };

  onWindowScroll = () => {
    this.$wrapper = document.querySelector(this.COLLECTION_WRAPPER_SELECTOR);
    if (!this.$wrapper) return;

    if (this.pagination_type !== 'infinite-scroll') {
      return;
    }

    const height = this.$wrapper.offsetTop + this.$wrapper.offsetHeight;
    if (height - this.PAGINATION_LOAD_OFFSET < (document.documentElement.scrollTop + window.innerHeight)) {
      this.load_next_page();
    }
  };

  check_pagination_progress = () => {
    const nextLink = document.querySelector(`${this.PAGINATION_SELECTOR} .pagination-next a`);
    const next_url = nextLink ? nextLink.getAttribute('href') : null;
    this.fully_loaded = (!next_url || next_url === '#');
  };

  build_load_more_html = function () {
    const pagination = document.querySelector('.pagination');
    if (!pagination) return;
    const totalItems = parseInt(pagination.getAttribute('data-paginate-items'), 10);
    const currentOffset = parseInt(pagination.getAttribute('data-paginate-offset'), 10);
    const pageSize = parseInt(pagination.getAttribute('data-paginate-size'), 10);
    const totalViewed = currentOffset + pageSize;
    const progressPercentage = (100 * totalViewed) / totalItems;
    let youViewed = "";

    if (this.theme.translations.youve_viewed !== undefined) {
      youViewed = this.theme.translations.youve_viewed.replace('{{ x }}', totalViewed).replace('{{ y }}', totalItems);
    }

    const buttonHtml = `
      <div class="pagination__load-more">
        <p class="pagination__progress-text">${youViewed}</p>
        <div class="progress-bar"><div class="progress-bar__inner" style="width:${progressPercentage}%;"></div></div>
        <button class="btn btn--load-more">
          <svg style="display:none" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="1.6em" height="1.6em" class="spin flex-full pagination-loading"><g clip-path="url(#clip0_3605_47041)"><path d="M12.5 23C6.42487 23 1.5 18.0751 1.5 12C1.5 5.92487 6.42487 1 12.5 1C18.5751 1 23.5 5.92487 23.5 12C23.5 15.1767 22.1534 18.0388 20 20.0468" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></g><defs><clipPath id="clip0_3605_47041"><rect width="24" height="24" fill="none" transform="translate(0.5)"/></clipPath></defs></svg>
          <span class="btn--load-more--text">${this.theme.translations.load_more}</span>
        </button>
      </div>
    `;

    return buttonHtml;
  };

  load_more_button = function () {
    this.check_pagination_progress();

    // Check if Load More button already on page
    const buttonAppended = document.querySelector('.pagination .btn--load-more') !== null;

    // Build Button html
    const buttonHtml = this.build_load_more_html();

    // If button not on page and more results are available, display button
    if (!buttonAppended && !this.fully_loaded) {
      document.querySelector('.pagination').insertAdjacentHTML('beforeend', buttonHtml);
    }

    // If button clicked, load next page
    const loadMoreButton = document.querySelector('.btn--load-more');
    if (loadMoreButton) {
      loadMoreButton.addEventListener('click', this.load_next_page.bind(this.$self));
    }
  };

  set_loading = (loading) => {
    this.$loading_indicator = document.querySelector('.pagination-loading');
    this.$load_more_button_text = document.querySelector('.btn--load-more--text');
    if (loading) {
      this.loading = true;
      if (this.$load_more_button_text) this.$load_more_button_text.style.visibility = 'hidden';
      if (this.$loading_indicator) this.$loading_indicator.style.display = 'block';
    } else {
      this.loading = false;
      if (this.$load_more_button_text) this.$load_more_button_text.style.visibility = 'visible';
      if (this.$loading_indicator) this.$loading_indicator.style.display = 'none';
    }
  };

  load_next_page = () => {
    if (this.loading) {
      return false;
    }
    const nextLink = document.querySelector(`${this.PAGINATION_SELECTOR} .pagination-next a`);
    const next_url = nextLink ? nextLink.getAttribute('href') : null;
    if (!next_url || next_url === '#') {
      return;
    }
    this.set_loading(true);
    fetch(next_url)
      .then(response => response.text())
      .then(this.page_loaded.bind(this))
      .catch(error => {
        console.error('Error loading next page:', error);
      })
      .finally(() => this.set_loading(false));
  };

  scroll_position_history = () => {
    const pathName = document.location.pathname;

    window.onbeforeunload = function () {
      const htmlContent = document.querySelector('.collection-main-body-inner').innerHTML;
      const paginationContent = document.querySelector('.pagination').innerHTML;
      sessionStorage.setItem('pagePosition_' + pathName, htmlContent.toString());
      sessionStorage.setItem('pagePagination_' + pathName, paginationContent.toString());
    }

    if (performance.navigation.type === 2 && sessionStorage['pagePosition_' + pathName]) {
      const htmlContentContainer = document.querySelector('.collection-main-body-inner');
      htmlContentContainer.innerHTML = sessionStorage.getItem('pagePosition_' + pathName);
    }

    if (performance.navigation.type === 2 && sessionStorage['pagePagination_' + pathName]) {
      const paginationContainer = document.querySelector('.pagination');
      paginationContainer.innerHTML = sessionStorage.getItem('pagePagination_' + pathName);
    }
  }

  page_loaded = (data) => {
    const parser = new DOMParser();
    const data_dom = parser.parseFromString(data, 'text/html');
    const gridClassesElement = document.getElementById('grid_item_width_classes');
    const classes = gridClassesElement ? gridClassesElement.value : '';
  
    if (classes) {
      const classList = classes.split(' '); // Split the classes into an array
      data_dom.querySelectorAll('.grid__item').forEach(item => {
        item.classList.remove('large--one-quarter', 'medium--one-quarter', 'small--one-whole', 'large--one-half', 'medium--one-half');
        classList.forEach(cls => item.classList.add(cls)); // Add each class individually
      });
    }
    this.$collection.append(
      ...data_dom.querySelectorAll(`${this.COLLECTION_HEADER_SELECTOR} > ${this.COLLECTION_ITEM_SELECTOR}:not(${this.COLLECTION_ITEM_EXCLUDE_SELECTOR})`),
      ...data_dom.querySelectorAll(`${this.COLLECTION_MAIN_SELECTOR} > ${this.COLLECTION_ITEM_SELECTOR}`)
    );
    const newPagination = data_dom.querySelector(this.PAGINATION_SELECTOR);
    if (newPagination) {
      document.querySelector(this.PAGINATION_SELECTOR).replaceWith(newPagination);
    }
  
    // If "load more" enabled, run function to conditionally render button on newly-loaded page
    if (this.pagination_type === 'load-more') {
      this.load_more_button();
    }
  
    this.execCallbacks();
  };
}
